import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import moment from 'moment-jalaali';
import { RouteComponentProps } from '@reach/router';
import { parse } from 'query-string';
import { TProjects } from 'store/companies/companiesTypes';
import { NumberOrNull, StringOrNull } from 'store/entity/types';
import { ITasks, RootState } from 'store/store';
import { PrintInfo, ReportContent, ChartSection, ReportTitle } from './styles';
import messages from './Messages';
import { display } from 'components/ErrorView/ErrorDialog';
import ReportChart from 'components/Report/ReportChart';
import protectedRoute from 'components/protectedRoute';
import ReportInfoBoxes from 'components/Report/ReportInfoBoxes';
import { getChartData } from '../TaskReport/TaskReport';
import { appDispatch } from 'store/store';
import {
  getCompanyFn,
  getCompanyHandler,
} from 'store/companies/companiesSlice';
import {
  ITemplateReportMark,
  ITemplateReportValues,
} from 'store/templates/templatesSlice';
import { getTaskReport, getTaskReportFn } from 'store/tasks/tasksSlice';
import { ITemplateReportOptions } from 'store/tasks/tasksType';
import cloneDeep from 'lodash/cloneDeep';
import { getCalendarLocal } from '../../../i18n';
import { ILocal } from '../../Chart/BaseDatePicker';

moment.loadPersian({ dialect: 'persian-modern' });

type StateToProps = {
  tasks: ITasks['data'];
  projects: TProjects;
};
type DispatchToProps = {
  getReport: (
    companyId: number,
    templateReport: ITemplateReportMark,
    startDate: number,
    toDate: number,
    projectId: NumberOrNull,
    parentId: NumberOrNull,
    templateId: number,
    assigneeId: NumberOrNull,
    splitMonths: number,
    splitField: StringOrNull,
    fieldValues: { [key: string]: string }
  ) => ReturnType<typeof getTaskReportFn>;
  requireCompany: (companyId: number) => ReturnType<typeof getCompanyFn>;
};

type ParamProps = {
  companyId: string;
};

type OwnProps = RouteComponentProps<ParamProps>;
type ComponentProps = OwnProps & StateToProps & DispatchToProps;
type ComponentState = {
  preparing: boolean;
  chartData: ITemplateReportValues[];
  tableData: ITemplateReportValues;
};

export function getTemplateReportMark(
  templateReportOptionsParam: ITemplateReportOptions
) {
  const templateReportOptions = cloneDeep(templateReportOptionsParam);
  delete templateReportOptions.mainProgressTitle;
  delete templateReportOptions.secondaryProgressTitle;
  delete templateReportOptions.verifyTitle;

  const reportMark: ITemplateReportMark = Object.keys(
    templateReportOptions
  ).reduce((marks: any, prop: string) => {
    marks[prop] = !!templateReportOptions[prop];
    return marks;
  }, {});
  return reportMark;
}

class PrintTaskReport extends React.PureComponent<
  ComponentProps,
  ComponentState
> {
  readonly state = {
    preparing: true,
    chartData: [],
    tableData: {
      startMonthIndex: 0,
      endMonthIndex: 0,
    },
  };

  componentDidMount() {
    let parsed: any;
    if (this.props.location) {
      parsed = parse(this.props.location.search);
    }
    const {
      startDate,
      toDate,
      projectId,
      parentId,
      templateId,
      assigneeId,
      splitMonths,
      splitField,
      showRelative,
      chartView,
      ...fieldValues
    } = parsed;
    const safeTemplateId = Number(templateId);
    const safeProjectId = projectId === 'undefined' ? null : projectId;
    const safeParentId = parentId === 'undefined' ? null : parentId;
    const safeAssigneeId = assigneeId === 'null' ? null : assigneeId;

    const companyId = Number(this.props.companyId);
    this.props
      .requireCompany(companyId)
      .then(() => {
        const reportMark = getTemplateReportMark(
          this.props.tasks[safeTemplateId].templateReportOptions!
        );

        this.props
          .getReport(
            companyId,
            reportMark,
            startDate,
            toDate,
            safeProjectId,
            safeParentId,
            safeTemplateId,
            safeAssigneeId,
            splitMonths,
            splitField,
            fieldValues || {}
          )
          .then(({ data }) => {
            const chartData: ITemplateReportValues[] = getChartData(
              data.split,
              this.props.projects,
              this.props.tasks,
              safeTemplateId!,
              splitField,
              splitMonths,
              showRelative !== 'false',
              getCalendarLocal() as ILocal,
              projectId
            );
            const tableData: ITemplateReportValues = data.total;
            this.setState({ preparing: false, chartData, tableData });
            setTimeout(window.print, 2000);
          })
          .catch((err) => {
            display(err);
          });
      })
      .catch((err) => {
        display(err);
      });
  }

  fakeOnClick = () => () => {
    //
  };

  render() {
    const { tasks, projects, location } = this.props;
    const { tableData, chartData } = this.state;
    let parsed: any;
    if (location) {
      parsed = parse(location.search);
    }
    const templateId = Number(parsed.templateId);
    const projectName = projects[Number(parsed.projectId)]?.name;
    const showRelative = parsed.showRelative;
    const startDate = moment(Number(parsed.startDate)).format('jD jMMMM jYYYY');
    const toDate = moment(Number(parsed.toDate)).format('jD jMMMM jYYYY');

    const templateReportOptions = tasks[templateId]?.templateReportOptions;

    return (
      <React.Fragment>
        <PrintInfo>
          <span>{messages.printWillStartAutomatically}</span>
        </PrintInfo>
        {this.state.preparing ? (
          <span>{messages.preparing} ...</span>
        ) : (
          <ReportContent>
            {Object.keys(this.state.tableData).length > 2 && (
              <ChartSection>
                <ReportTitle>
                  <span>
                    {
                      templateReportOptions?.[
                        `${parsed.chartView}ProgressTitle`
                      ]
                    }{' '}
                    {projectName}
                  </span>
                  <span>
                    {messages.from} {startDate} {messages.to} {toDate}
                  </span>
                </ReportTitle>
                <ReportInfoBoxes
                  type={parsed.chartView}
                  templateReportOptions={templateReportOptions!}
                  tableData={tableData}
                  horizontal={true}
                />
                <ReportChart
                  type={parsed.chartView}
                  chartData={chartData}
                  templateReportOptions={templateReportOptions!}
                  hasTooltip={false}
                  onClickOnBar={this.fakeOnClick}
                  showRelative={showRelative !== 'false'}
                  printable={true}
                />
              </ChartSection>
            )}
          </ReportContent>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<StateToProps, OwnProps, RootState> = (
  state
) => {
  const tasks = state.tasks.data;
  const projects: TProjects = {};
  Object.values(state.companies.data).forEach((cmp) => {
    Object.assign(projects, cmp.projects);
  });

  return { tasks, projects };
};

const mapDispatchToProps: MapDispatchToProps<
  DispatchToProps,
  OwnProps
> = () => {
  return {
    requireCompany: (companyId: number) =>
      appDispatch(getCompanyHandler({ companyId })),
    getReport: (
      companyId: number,
      templateReport: ITemplateReportMark,
      startDate: number,
      toDate: number,
      projectId: NumberOrNull,
      parentId: NumberOrNull,
      templateId: number,
      assigneeId: NumberOrNull,
      splitMonths: number,
      splitField: StringOrNull,
      fieldValues: { [key: string]: string }
    ) =>
      appDispatch(
        getTaskReport({
          companyId,
          templateReport,
          startDate,
          toDate,
          projectId,
          parentId,
          templateId,
          assigneeId,
          splitMonths,
          splitField,
          fieldValues,
        })
      ),
  };
};

export default protectedRoute(
  connect(mapStateToProps, mapDispatchToProps)(PrintTaskReport)
);
